/**
 * ## ItemProviderWithStore.tsx ##
 * This file contains ItemProviderWithStore component
 * @packageDocumentation
 */
import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import { ItemProvider, ItemProviderProps, UpdatedItemProviderProps } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { equal } from '@common/typescript/Utils';
import { ItemState, TypeKeys } from '@common/react/store/ItemProviderWithStore';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';

interface ItemProviderWithStoreProps<T extends WithDeleted> extends ItemProviderProps<T> {
	/**
	 * property in redux that stores data for ItemProviderWithStore.
	 */
	storageName: string;
}
/**
 * ItemProviderWithStore component.
 *
 * @example ItemProviderWithStore with ReactNode inside:
 * <ItemProviderWithStore<T> loadRequest="request">{React.ReactNode}</ItemProviderWithStore>
 * @example ItemProviderWithStore with render function as children:
 * <ItemProviderWithStore<T> loadRequest="request">{(context) => React.ReactNode}</ItemProviderWithStore>
 *
 * @typeParam T - T Any {@link WithDeleted}
 * @param props - {@link ItemProviderWithStoreProps}
 * @type {React.FC<ItemProviderWithStoreProps>}
 * @returns React.ReactElement
 */
const ItemProviderWithStore: <T extends WithDeleted>(
	props: UpdatedItemProviderProps<ItemProviderWithStoreProps<T>>
) => React.ReactElement<T> = <T extends WithDeleted>(
	props: UpdatedItemProviderProps<ItemProviderWithStoreProps<T>>,
) => {
	const {
		storageName,
		additionalParams = {},
		...rest
	} = props;
	const dispatch = useDispatch();
	const itemFromStore: ItemState<T> = useSelector((state) => state[storageName], shallowEqual);

	const isEqualParams = React.useMemo(() => {
		return (rest.id === -1 || rest.id === itemFromStore.item?.id)
			&& ((!additionalParams && !itemFromStore.params) || equal(additionalParams, itemFromStore.params));
	}, [itemFromStore.params, rest.id]);

	if (itemFromStore && itemFromStore.isLoading && !rest.item) {
		return <Loader />;
	}

	const item = isEqualParams ? itemFromStore.item : rest.item;
	return <ItemProvider
		showLoader={false}
		{...rest}
		additionalParams={additionalParams}
		item={item}
		add={item}
		syncSetItem={(value) => {
			dispatch({
				type: TypeKeys.UPDATEITEM,
				item: typeof value === 'function' ? value(item || ({ id: rest.id } as any)) : value,
				storageName,
			});
		}}
		beforeLoad={(params) => {
			dispatch({
				type: TypeKeys.REQUESTITEM,
				params,
				objectType: rest.loadRequest,
				storageName,
			});
		}}
		onLoad={(res, params) => {
			rest.onLoad?.(res, params);
			dispatch({
				type: TypeKeys.SETITEM,
				params,
				objectType: rest.loadRequest,
				item: res,
				storageName,
			});
		}}
		skipInitLoad={isEqualParams && itemFromStore.item ? true : rest.skipInitLoad}
	>
		{rest.children}
	</ItemProvider>;
};

export default ItemProviderWithStore;
